import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import LoadingBar from "../LoadingBar";
import ProfileWithBackButtonHeader from "../header/ProfileWithBackButtonHeader";
import getfile from "../../utils/getFile";

import MissionsCard from "./missionsCard";

import "../../App.css";
import { useLiffProfile } from "../../context/LiffProfileContext";

// เพิ่มการ import data
//import missionsData from "./data.json"; // <<< important line

const Missions = () => {
  const { t, i18n } = useTranslation();
  const { authToken, userId } = useLiffProfile();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const [imageUrls, setImageUrls] = useState({});

  useEffect(() => {
    setLoading(true);

    const apiUrl =
      activeTab === "completed"
        ? `${process.env.REACT_APP_API_URL}/api/clm-missions?completed=true&orderBy=priority:ASC`
        : `${process.env.REACT_APP_API_URL}/api/clm-missions?orderBy=priority:ASC`;
    const clientAuth = process.env.REACT_APP_CLIENT_AUTH;

    axios
      .get(apiUrl, {
        headers: {
          Authorization: "Bearer " + authToken,
          "X-Client-Auth": clientAuth,
          "x-user-id": userId,
          "Accept-Language": i18n.language.toUpperCase(),
        },
      })
      .then((response) => {
        if (response.data && response.data.data) {
          setData(response.data.data);
          const items = response.data.data.items;
          const imagePromises = items.map((item) => {
            if (item.image) {
              return getfile(item.image, authToken, userId).then((url) => ({
                id: item.id,
                url,
              }));
            }
            return Promise.resolve(null);
          });

          Promise.all(imagePromises).then((results) => {
            const imageMap = {};
            results.forEach((result) => {
              if (result) {
                imageMap[result.id] = result.url;
              }
            });
            setImageUrls(imageMap);
          });
        } else {
          setError("No data found in response.");
        }
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [activeTab, authToken, userId]);

  /** 
  useEffect(() => {
    // ดึงข้อมูลจากไฟล์ data.json
    try {
      setData(missionsData.data); // << set data จาก data.json
    } catch (err) {
      setError("Failed to load test data");
    }
  }, [activeTab]);
 **/
  const handleTabClick = (tabValue) => {
    setActiveTab(tabValue);
  };

  const tabs = [
    { key: t("mission_status_all"), value: "all" },
    { key: t("mission_status_completed"), value: "completed" },
  ];

  const filteredData = data
    ? activeTab === "completed"
      ? data.items.filter((item) => item.isCompleted)
      : data.items
    : [];
  const groupedByCategory = filteredData.reduce((acc, item) => {
    if (!acc[item.category]) {
      acc[item.category] = [];
    }
    acc[item.category].push(item);
    return acc;
  }, {});

  const latestItemsByCategory = Object.keys(groupedByCategory)
    .map(
      (category) =>
        groupedByCategory[category][groupedByCategory[category].length - 1]
    )
    .sort((a, b) => {
      if (a.status !== b.status) return a.status.localeCompare(b.status);
      if (a.isCompleted !== b.isCompleted) return a.isCompleted - b.isCompleted;
      if (a.targeted !== b.targeted) return b.targeted - a.targeted;
      if (a.dayToExpire !== b.dayToExpire) return a.dayToExpire - b.dayToExpire;
      const aProgressRatio = (a.progressValue || 0) / (a.completeValue || 1);
      const bProgressRatio = (b.progressValue || 0) / (b.completeValue || 1);
      return aProgressRatio - bProgressRatio;
    });

  if (loading) {
    return (
      <div className="w-full h-screen flex items-center justify-center">
        <LoadingBar loading={true} label="Loading..." />
      </div>
    );
  }

  if (error) return <p className="text-center text-red-500">{error}</p>;

  return (
    <div className="w-full flex flex-col gap-y-4">
      <div className="h-screen flex flex-col bg-secondary-background">
        <div className="flex-grow overflow-y-auto">
          <ProfileWithBackButtonHeader className="bg-primary" />
          <div className="w-full flex flex-col px-2 py-2 bg-primary">
            <h2 className="text-3xl pt-3 pb-3 font-bold">
              {t("big_c_mission")}
            </h2>
          </div>

          {/* Tabs */}
          <div className="flex space-x-4 w-full border-b mt-8">
            {tabs.map((tab) => (
              <div
                key={tab.value}
                className={`cursor-pointer text-22 sm:text-base font-medium w-full text-center ${
                  activeTab === tab.value
                    ? "border-b-2 border-primary"
                    : "text-gray-700"
                }`}
                onClick={() => handleTabClick(tab.value)}
              >
                {tab.key}
              </div>
            ))}
          </div>

          {/* Missions List */}
          {error ? (
            <p className="text-red-500">{error}</p>
          ) : (
            <div className="px-4 py-2 font-bold">
              {latestItemsByCategory.length > 0 ? (
                latestItemsByCategory.map((item) => {
                  const isExpiredMission =
                    (item.dayToExpire ?? -1) < 0 || item.status === "E";
                  const missionsInCategory = groupedByCategory[
                    item.category
                  ] || [item];

                  const actionLink =
                    missionsInCategory.length > 1
                      ? `/missions/${item.category}`
                      : `/missions/detail/${item.id}`;

                  return (
                    <MissionsCard
                      key={item.id}
                      item={item}
                      missionsInCategory={missionsInCategory}
                      imageUrls={imageUrls}
                      isExpiredMission={isExpiredMission}
                      templateDetail={false}
                      actionLink={actionLink}
                      showDescription={false}
                    />
                  );
                })
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center">
                  <img
                    src={require("../../assets/images/mission-biggy-bear.0da057a.png")}
                    alt="No Missions"
                    className="w-40 mt-10"
                  />
                  <div className="mt-5 text-center">
                    <span className="font-semibold text-22">
                      {t("mission_empty_header")}
                    </span>
                    <br />
                    <span className="font-normal">
                      {t("mission_empty_desc")}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Missions;
